import { defaultHighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { diff } from "@codemirror/merge";
import { EditorState as CodeMirrorState, Compartment } from "@codemirror/state";
import { EditorView, lineNumbers } from "@codemirror/view";
import { CodeMirror, CodeMirrorEditor, react } from "@handlewithcare/react-codemirror";
import {
	type NodeViewComponentProps,
	useEditorEventCallback,
	useIsNodeSelected,
} from "@handlewithcare/react-prosemirror";
import { xcodeDark, xcodeLight } from "@uiw/codemirror-theme-xcode";
import cx from "classnames";
import { NodeSelection } from "prosemirror-state";
import { forwardRef, useMemo, useState } from "react";

import { CloseFullscreenIcon, GitHubIcon } from "@moment/icons";
import { BasicTooltip, IconButton, TooltipContent, TooltipTrigger, cn, useTheme } from "@moment/ui";

import { useCodeSnippet } from "../LinkCodeSnippet/useCodeSnippet";
import { getLanguageExtension } from "../codeBlock/languageExtensions";

const themeCompartment = new Compartment();

// Build extensions similar to CodeBlock.tsx but simplified for read-only
const buildExtensions = (language: string, startLine: number) => {
	const languageExtension = getLanguageExtension(language);

	return [
		themeCompartment.of([]),
		syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
		languageExtension,
		lineNumbers({
			formatNumber: (lineNo) => String(startLine + lineNo - 1),
		}),
		EditorView.editable.of(false),
		EditorView.lineWrapping,
		react,
	];
};

export const CodeSnippetBlock = forwardRef<HTMLDivElement, NodeViewComponentProps>(
	function CodeSnippetBlock({ nodeProps, children: _, ...props }, ref) {
		const { node, getPos } = nodeProps;

		const selected = useIsNodeSelected();

		const url = node.attrs["url"] as string;
		const owner = node.attrs["owner"] as string;
		const repo = node.attrs["repo"] as string;
		const refspec = node.attrs["refspec"] as string;
		const filePath = node.attrs["filePath"] as string;
		const startLine = node.attrs["startLine"] as number;
		const endLine = (node.attrs["endLine"] as number | null) ?? null;

		const { lines, isLoading, error } = useCodeSnippet(
			owner,
			repo,
			refspec,
			filePath,
			startLine,
			endLine
		);

		const theme = useTheme();

		// Determine language from file extension
		const language = useMemo(() => {
			const ext = filePath.split(".").pop()?.toLowerCase() || "";
			return ext;
		}, [filePath]);

		// Create CodeMirror state - similar to CodeBlock.tsx
		const [codeMirrorState, setCodeMirrorState] = useState(() =>
			CodeMirrorState.create({
				doc: "",
				extensions: buildExtensions(language, startLine),
			})
		);

		// Update content when lines change - similar to CodeBlock.tsx
		const incomingCode = !isLoading && !error && lines.length > 0 ? lines.join("\n") : "";
		if (incomingCode !== codeMirrorState.doc.toString()) {
			setCodeMirrorState((prev) => {
				const current = prev.doc.toString();
				const diffed = diff(current, incomingCode);

				return prev.update({
					changes: diffed.map((change) => ({
						from: change.fromA,
						to: change.toA,
						insert: incomingCode.slice(change.fromB, change.toB),
					})),
				}).state;
			});
		}

		// Update theme when it changes - similar to CodeBlock.tsx
		const codeMirrorTheme = themeCompartment.get(codeMirrorState);
		const effects = [];

		if (theme.resolvedTheme === "light" && codeMirrorTheme !== xcodeLight) {
			effects.push(themeCompartment.reconfigure(xcodeLight));
		} else if (theme.resolvedTheme !== "light" && codeMirrorTheme !== xcodeDark) {
			effects.push(themeCompartment.reconfigure(xcodeDark));
		}

		if (effects.length) {
			setCodeMirrorState(
				(prev) =>
					prev.update({
						effects,
					}).state
			);
		}

		// Handle clicks on the CodeMirror to select the node
		const handleCodeMirrorMouseDown = useEditorEventCallback((view, e) => {
			const pos = getPos();
			if (pos !== undefined) {
				const tr = view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos));
				view.dispatch(tr);
				view.focus();
			}
		});

		return (
			<div data-text-cell={node.attrs["id"]}>
				<div
					ref={ref}
					{...props}
					className={cx(
						"relative border bg-palette-gray-3 px-1.5 pb-1.5 rounded-lg mb-4",
						{
							"ring-1 ring-brand border-brand": selected,
						}
					)}
				>
					<div className="flex items-center gap-2 py-1">
						<GitHubIcon className="ml-1" />
						<div className="min-w-0 truncate text-xs text-secondary">
							<a
								className="text-primary hover:text-primary font-medium text-sm"
								href={url}
								target="_blank"
								rel="noopener noreferrer"
							>
								{filePath}
							</a>{" "}
							{startLine > 0
								? `lines ${startLine}${endLine ? ` to ${endLine}` : ""}`
								: ""}{" "}
							· {owner}/{repo} @{" "}
							<a
								href={`https://github.com/${owner}/${repo}/tree/${refspec}`}
								className="text-primary hover:text-primary text-xs font-mono font-medium"
								target="_blank"
								rel="noopener noreferrer"
							>
								{refspec}
							</a>
						</div>
						<div className="flex items-center gap-1 ml-auto">
							<BasicTooltip>
								<TooltipTrigger asChild>
									<IconButton
										preset="quaternary"
										label="Collapse code snippet"
										size="sm"
										icon={<CloseFullscreenIcon />}
										onClick={useEditorEventCallback((view, e) => {
											e?.preventDefault?.();
											e?.stopPropagation?.();
											const pos = getPos();
											const tr = view.state.tr.replaceWith(
												pos,
												pos + view.state.doc.nodeAt(pos)!.nodeSize,
												view.state.schema.nodes.code_snippet.create({
													...node.attrs,
												})
											);
											view.dispatch(tr);
										})}
									/>
								</TooltipTrigger>
								<TooltipContent>Collapse code snippet</TooltipContent>
							</BasicTooltip>
						</div>
					</div>
					<div className="bg-primary rounded-md" onMouseDown={handleCodeMirrorMouseDown}>
						{isLoading ? (
							<div className="p-2 text-xs text-secondary">Loading code…</div>
						) : error ? (
							<div className="p-2 text-xs text-secondary">Failed to load code</div>
						) : !isLoading && !error && lines.length > 0 ? (
							<CodeMirror
								state={codeMirrorState}
								extensions={buildExtensions(language, startLine)}
							>
								<CodeMirrorEditor
									className={cn(
										"[&>.cm-editor]:!bg-transparent",
										"[&>.cm-editor]:!scroll-mr-10",
										"[&>.cm-focused]:!outline-none",
										"[&_.cm-editor_.cm-scroller]:font-mono",
										"[&_.cm-editor_.cm-scroller]:overflow-hidden",
										"font-normal text-xs",
										"[&_.cm-line]:leading-[1.4]",
										"[&_.cm-editor_.cm-scroller]:overflow-x-auto",
										"[&_.cm-editor_.cm-scroller]:overscroll-x-none",
										"[&_.cm-editor_.cm-scroller]:bg-transparent",
										"[&_.cm-editor_.cm-scroller]:rounded-md",
										"[&_.cm-editor_.cm-scroller]:-m-px",
										"[&_.cm-editor_.cm-scroller]:border",
										"[&_.cm-editor_.cm-scroller]:border-transparent",
										"[&_.cm-editor_.cm-scroller_.cm-gutters]:bg-primary",
										"[&_.cm-editor_.cm-scroller_.cm-gutters]:text-tertiary",
										"[&_.cm-editor_.cm-scroller_.cm-gutters]:pb-2",
										"[&_.cm-editor_.cm-scroller_.cm-gutters_.cm-gutterElement]:pl-2",
										"[&_.cm-editor_.cm-scroller_.cm-activeLine]:bg-transparent",
										"[&_.cm-editor.cm-focused_.cm-scroller_.cm-activeLine]:bg-palette-gray-3",
										"[&_.cm-editor_.cm-cursor]:border-l-[2px]",
										"[&_.cm-editor_.cm-cursor]:border-palette-gray-12",
										"[&_.cm-editor_.cm-scroller_.cm-content:has(>_:only-child)_.cm-activeLine]:!bg-transparent",
										"[&_.cm-editor.cm-focused_.cm-scroller_.cm-content:has(>_:only-child)_.cm-activeLine]:!bg-transparent",
										"[&_.cm-editor_.cm-scroller_.cm-activeLineGutter]:bg-transparent",
										"[&_.cm-editor.cm-focused_.cm-scroller_.cm-activeLineGutter]:bg-palette-gray-3",
										"[&_.cm-editor_.cm-scroller_.cm-content]:py-2",
										"[&_.cm-editor_.cm-cursor]:hidden"
									)}
								/>
							</CodeMirror>
						) : null}
					</div>
				</div>
			</div>
		);
	}
);
